import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSurveyResponsesLaureatByUserId } from "../../../services/surveyResponse.service";
import SignDocs from "../SignDocs";
import { useAuth } from "../../../contexts/AuthContext";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import CountUp from "react-countup";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import {
  BellIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DocsSigné from "../DocsSigné";
import { laureatStatistics } from "../../../services/statistics.service";
import GetStatusLaureat from "../../../helpers/getStatusLaureat";

const LaureatStatistics = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalApplications, setTotalApplications] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openSignDocs, setOpenSignDocs] = useState(false);
  const [openDocsSigné, setOpenDocsSigné] = useState(false);
  const [inProgressEvaluation, setInProgressEvaluation] = useState(0);
  const [submittedApplications, setSubmittedApplications] = useState(0);
  const [drafts, setDrafts] = useState(0);
  const [responseId, setResponseId] = useState();
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);

  useEffect(() => {
    const fetchSurveyResponses = async () => {
      try {
        const queryParams = { page: page + 1, limit: rowsPerPage };
        const response = await getSurveyResponsesLaureatByUserId(
          userId,
          queryParams
        );
        setApplications(response.surveyResponses);
        setFilteredApplications(response.surveyResponses);
        setTotalApplications(response.total);
      } catch (error) {
        console.error("Error fetching survey responses:", error.message);
      }
    };

    fetchSurveyResponses();
  }, [userId, page, rowsPerPage]);

  const fetchStatistics = useCallback(async () => {
    if (isLoadingStatistics) return;

    setIsLoadingStatistics(true);
    try {
      const response = await laureatStatistics(userId, null);
      setInProgressEvaluation(response.totalEnCoursEvaluation);
      setSubmittedApplications(response.totalSoumis);
      setDrafts(response.totalBrouillon);
    } catch (error) {
      console.error("Error fetching laureat statistics:", error.message);
    } finally {
      setIsLoadingStatistics(false);
    }
  }, [userId, applications]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  useEffect(() => {
    const filterApplications = () => {
      let filtered = applications;

      if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        filtered = filtered.filter((app) => {
          const matchesTitle = app.program.title
            .toLowerCase()
            .includes(lowerCaseQuery);
          const matchesId = app.id.toString().includes(lowerCaseQuery);
          const matchesDate = formatDate(app.createdAt)
            .toLowerCase()
            .includes(lowerCaseQuery);
          return matchesTitle || matchesId || matchesDate;
        });
      }

      if (statusFilter) {
        filtered = filtered.filter((app) => app.status === statusFilter);
      }

      setFilteredApplications(filtered);
    };

    filterApplications();
  }, [searchQuery, statusFilter, applications]);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const handleSearchInputChange = debounce((e) => {
    setSearchQuery(e.target.value);
  }, 300);

  const handleCheckboxChange = (applicationId) => {
    setSelectedApplications((prevSelected) =>
      prevSelected.includes(applicationId)
        ? prevSelected.filter((id) => id !== applicationId)
        : [...prevSelected, applicationId]
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // const handleOpenDocsSigné = (url) => {
  //   setPdfUrl(url);
  //   setOpenDocsSigné(true);
  // };

  const handleDetailsClick = (app) => {
    if (
      [
        "SOUMIS",
        "Rejeté",
        "À Évaluer",
        "Éligible",
        "Sélectionné",
        "Retenue",
        "En cours d'évaluation",
        "Processus terminé"
      ].includes(app?.status)
    ) {
      navigate(`/laureat-dashboard/details/${app?.id}/${app?.status}`);
    } else {
      localStorage.setItem("programId", app?.program?.id);
      navigate("/laureat-dashboard/fill-form");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  return (
    <div className="mx-auto px-6 py-3 bg-card text-foreground  bg-slate-100 h-full">
      <div className="flex justify-between items-center ">
        <div>
          <h2 className="text-2xl font-bold mb-0">Tableau de bord</h2>
          <p className="text-gray-500 text-sm mb-1">
            Suivez et gérez les informations et les activités des clients.
          </p>
        </div>
      </div>
      <hr className="mt-0" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="bg-card px-4 py-3 rounded-lg shadow-md border border-slate-400 transition duration-300 hover:scale-105 bg-white">
          <h2 className="text-base font-normal text-gray-700 flex items-center gap-3">
            Total des applications
            <InformationCircleIcon className="w-5 h-5" />
          </h2>
          <p className="text-2xl font-bold text-[#0976BC]">
            <CountUp end={totalApplications} duration={2} />
          </p>
          <hr />
          <select className="mt-2 bg-input text-muted-foreground rounded">
            <option>Ce mois</option>
          </select>
        </div>

        <div className="bg-card p-4 rounded-lg shadow-md border border-slate-400 transition duration-300 hover:scale-105 bg-white">
          <h2 className="text-base font-normal text-gray-700 flex items-center gap-3">
            Candidatures soumis
            <InformationCircleIcon className="w-5 h-5" />
          </h2>
          <p className="text-2xl font-bold text-[#0976BC]">
            <CountUp end={submittedApplications} duration={1.5} />
          </p>
          <hr />
          <select className="mt-2 bg-input text-muted-foreground rounded">
            <option>Ce mois</option>
          </select>
        </div>
        <div className="bg-card p-4 rounded-lg shadow-md border border-slate-400 transition duration-300 hover:scale-105 bg-white">
          <h2 className="text-base font-normal text-gray-700 flex items-center gap-3">
            Candidatures Brouillon
            <InformationCircleIcon className="w-5 h-5" />
          </h2>
          <p className="text-2xl font-bold text-[#0976BC]">
            <CountUp end={drafts} duration={1.5} />
          </p>
          <hr />
          <select className="mt-2 bg-input text-muted-foreground rounded">
            <option>Ce mois</option>
          </select>
        </div>
        <div className="bg-card p-4 rounded-lg shadow-md border border-slate-400 transition duration-300 hover:scale-105 bg-white">
          <h2 className="text-base font-normal text-gray-700 flex items-center gap-3">
            En cours d’évaluation
            <InformationCircleIcon className="w-5 h-5" />
          </h2>
          <p className="text-2xl font-bold text-[#0976BC]">
            <CountUp end={inProgressEvaluation} duration={1.5} />
          </p>
          <hr />
          <select className="mt-2 bg-input text-muted-foreground rounded">
            <option>Ce mois</option>
          </select>
        </div>
      </div>

      <div className="border rounded-lg p-3 bg-white mt-4">
        <div className="flex flex-col-reverse md:flex-row justify-between md:items-center mb-3">
          <div className="relative  w-full md:w-1/4">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
            </div>
            <input
              type="search"
              id="default-search"
              onChange={handleSearchInputChange}
              className="py-[12px] ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg focus:outline-none w-full"
              placeholder="Rechercher par appel à candidature"
              required
            />
          </div>

          {/* <div className="flex justify-end mb-2 md:mb-0 md:items-center space-x-4">
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="py-2 px-4 border border-gray-300 rounded-md text-gray-600"
            >
              <option value="">Tous les statut</option>
              <option value="Retenue">Retenue</option>
              <option value="SOUMIS">Soumis</option>
              <option value="Brouillon">Brouillon</option>
              <option value="À Évaluer">À Évaluer</option>
              <option value="Sélectionné">Sélectionné</option>
              <option value="Rejeté">Rejeté</option>
            </select>
          </div> */}
        </div>
        <main className="flex-1 min-w-0">
          <div className="w-full overflow-hidden">
            <TableContainer
              component={Paper}
              className="w-full table-auto overflow-x-auto"
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedApplications.length > 0 &&
                          selectedApplications.length < applications?.length
                        }
                        checked={
                          selectedApplications.length > 0 &&
                          selectedApplications.length === applications?.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedApplications(
                              applications?.map((app) => app.id)
                            );
                          } else {
                            setSelectedApplications([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Ref ID</TableCell>
                    <TableCell>Appel à candidature</TableCell>
                    <TableCell>Date d'application</TableCell>
                    <TableCell>Date de réponse</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Documents annexes</TableCell>
                    <TableCell>Convention</TableCell>
                    {/*<TableCell>Formulaire juridique</TableCell> */}
                    <TableCell>Détails</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications?.map((app) => (
                    <TableRow
                      key={app.id}
                      hover
                      selected={selectedApplications.includes(app.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedApplications.includes(app.id)}
                          onChange={() => handleCheckboxChange(app.id)}
                        />
                      </TableCell>
                      <TableCell>{`${app.id}`}</TableCell>
                      <TableCell>{app.program.title}</TableCell>
                      <TableCell>{formatDate(app.createdAt)}</TableCell>
                      <TableCell>{formatDate(app.updatedAt)}</TableCell>
                      <TableCell>
                        <GetStatusLaureat status={app.status} />
                      </TableCell>
                      <TableCell>
                        {app?.documentAnnexe ? (
                          <>
                            {app?.annexeDocsResponses &&
                            Object.keys(app.annexeDocsResponses).length > 0 ? (
                              // <Link
                              //   to={`formulaireCadrage/details/${app?.id}`}
                              //   // className="no-underline bg-[#0976BC] hover:bg-[#065A94] transition-colors duration-300 text-white px-2 py-1 rounded-full text-xs flex items-center justify-center"
                              //   className="no-underline bg-[#0976BC] hover:bg-[#065A94] transition-colors duration-300 capitalize text-white font-normal shadow-none px-[12px] py-[6px] rounded-full text-xs"

                              //   style={{ minWidth: "50px", whiteSpace: "nowrap" }}
                              // >
                              //   Détails
                              // </Link>
                              <span style={{ color: "green" }} className="flex">
                                Documents annexe <br /> envoyé
                              </span>
                            ) : (
                              <Link
                                to={`/laureat-dashboard/formulaireCadrage/${app?.id}`}
                                // className="no-underline bg-[#0976BC] hover:bg-[#065A94] transition-colors duration-300 text-white px-2 py-1 rounded-full text-xs flex items-center justify-center"
                                className="no-underline bg-[#0976BC] hover:bg-[#065A94] transition-colors duration-300 capitalize text-white font-normal shadow-none px-[12px] py-[6px] rounded-full text-xs"
                                style={{
                                  minWidth: "50px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                importer les documents annexes
                              </Link>
                            )}
                          </>
                        ) : (
                          <span>---</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {app?.conventionStatus === "SIGNÉ" ? (
                          <span
                            style={{
                              color: "green",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            className="flex"
                            onClick={() => {
                              setOpenDocsSigné(true);
                              setResponseId(app?.id);
                            }}
                          >
                            Convention <br /> signée
                          </span>
                        ) : app?.conventionStatus === "NON SIGNÉ" ? (
                          <span
                            style={{
                              color: "red",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            className=" flex p-auto"
                            onClick={() => {
                              setOpenDocsSigné(true);
                              setResponseId(app?.id);
                            }}
                          >
                            Convention <br /> non signée
                          </span>
                        ) : (
                          <span style={{ color: "black" }} className=" flex">
                            ---
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <span
                          className="text-[#FF0000] underline text-sm font-medium cursor-pointer"
                          onClick={() => handleDetailsClick(app)}
                          style={{ minWidth: "50px", whiteSpace: "nowrap" }}
                        >
                          Détails ↗
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalApplications}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </div>
        </main>
      </div>

      {openSignDocs && <SignDocs onClose={() => setOpenSignDocs(false)} />}
      {openDocsSigné && (
        <DocsSigné
          survey_response_id={responseId}
          onClose={() => setOpenDocsSigné(false)}
        />
      )}
    </div>
  );
};

export default LaureatStatistics;
